/**
 * Hand — the viewer's own hand (ui/31 §2/§3). Never rendered for the
 * opponent (whose hand is redacted to id + count upstream anyway — ui/31 §4).
 * Clicking a <HandCard> only toggles local selection (useState in
 * `<GameView>`); it does not itself emit an Action — placement is a two-step
 * gesture (select card, then click an empty own cell).
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactElement } from 'react';
import type { CardInstance, CardType, InstanceId } from '@cards/engine';
import { CardInfoDialog, CardInfoPopover, type CardInfoData } from './CardInfoPopover.js';
import {
  applyCardPointerMotion,
  cardDragMotionVariables,
  compactBattleLayout,
  createCardDragMotion,
  liftCardDragMotionFromRect,
  previewAnchorGap,
  resetCardPointerMotion,
  setCardDragTarget,
  startCardDragAnimation,
  type CardDragMotion,
} from './cardMotion.js';
import { flightOriginFromPreview, type CardFlightApi } from './cardFlight.js';
import { invalidateDropZones, pinDropZoneRect, resolveDropCandidate } from './dropZones.js';
import { moveHandCardToIndex, nearestHandSlotIndex, reconcileHandOrder, type HandLayoutSlot } from './handOrder.js';
import { cardDescription, cardName } from './locale.js';
import { HandCardFace } from './HandCardFace.js';
import { useDesktopHover } from './useDesktopHover.js';
import { FULL_CARD_HOVER_OPEN_DELAY_MS, useHoverPopover } from './useHoverPopover.js';
import { Button } from './kit/index.js';
import { MOTION_DURATIONS } from './kit/tokens.js';

/** One hand card's render data: the instance plus its definition's cost/type. */
export interface HandCardData {
  instance: CardInstance;
  cost: number | undefined;
  type: CardType | undefined;
  hasStats: boolean;
  keywords: readonly string[];
}

function cardInfoData(card: HandCardData): CardInfoData {
  return {
    defId: card.instance.defId,
    ...(card.type === undefined ? {} : { type: card.type }),
    ...(card.instance.isToken === true ? { token: true } : {}),
    ...(card.cost === undefined ? {} : { cost: card.cost }),
    ...(card.hasStats ? {
      attack: card.instance.stats.attack,
      health: card.instance.stats.health,
      maxHealth: card.instance.stats.maxHealth,
    } : {}),
    keywords: card.keywords,
  };
}

/**
 * Viewport centre a drawn card flies from: straight below the bottom edge, in
 * the column of the slot it lands in. It used to leave the viewer's deck pile,
 * but that pile is a 38×18 counter in the panel — a card the size of a hand card
 * cannot read as coming out of it, so the trip only looked like the card
 * materialising mid-screen. Rising in from outside the viewport says "dealt to
 * you" without pointing at anything it did not come from; a real pile to fly
 * from can replace this later.
 *
 * Below rather than beside: the hand is anchored to the bottom edge, so this is
 * the one direction where the trip is short. Entering from the right meant ~760px
 * of travel on a desktop board, and the fly-home spring (tuned for hand↔cell
 * hops) sailed a card and a half past the slot before easing back.
 */
function offscreenDrawOrigin(slot: DOMRect): { x: number; y: number } {
  const viewportHeight = window.innerHeight > 0 ? window.innerHeight : document.documentElement.clientHeight;
  // A full card height past the edge, so the ghost is entirely off-screen when it
  // sets off and rises into view instead of fading in at the boundary.
  return {
    x: slot.left + slot.width / 2,
    y: viewportHeight + slot.height,
  };
}

export interface HandProps {
  cards: HandCardData[];
  /** Cards already assigned to an own staged cell; hidden from the hand while they occupy the board. */
  stagedIds: ReadonlySet<InstanceId>;
  /** Energy still available after the currently staged plan, including current-round temporary energy. */
  remainingEnergy: number;
  selectedId: InstanceId | undefined;
  onSelect: (instanceId: InstanceId) => void;
  draggingId: InstanceId | undefined;
  planningDisabled: boolean;
  onPointerDragStart: (instanceId: InstanceId) => void;
  /** Resolve the release target; GameView also records the matching flight destination. */
  onPointerDrop: (instanceId: InstanceId, clientX: number, clientY: number) => void;
  onPointerDragEnd: () => void;
  /** Staged board card currently being returned to the hand. */
  returnDropId: InstanceId | undefined;
  /** Shared flight system (GameView-owned): released cards fly to their new place. */
  flights: CardFlightApi;
  /** Explicit playback cue for a card just drawn into this hand. */
  drawCue: { instanceId: InstanceId; sequence: number } | undefined;
}

interface PointerDrag {
  instanceId: InstanceId;
  pointerId: number;
  startX: number;
  startY: number;
  motion: CardDragMotion;
  /** GameView's shared drag state and the floating preview have started. */
  started: boolean;
  /** The drag became a real carry; the slop radius no longer applies. */
  carried: boolean;
  /** Last position the finger was seen at, so a cancelled carry can still land. */
  lastX: number;
  lastY: number;
}

interface DragPreview {
  instanceId: InstanceId;
  motion: CardDragMotion;
}

/**
 * The hand's physical slots stay put while CSS `order` moves cards between
 * them. Capture their viewport centres once per carry, rather than measuring
 * every card at the pointer event rate.
 */
interface HandReorderGeometry {
  left: number;
  right: number;
  top: number;
  bottom: number;
  margin: number;
  slots: readonly HandLayoutSlot[];
}

/** Pointer jitter inside this radius still resolves as a click/tap on release. */
const POINTER_TAP_SLOP = 8;
/** Fallback reach of the reorder zone past the hand list when it has no measured box. */
const REORDER_ZONE_MARGIN = 90;
/** Everything a carried card can be released onto: board cells, locations, the whole stage. */
const DROP_ZONE_SELECTOR =
  '[data-drop-enabled="true"], [data-table-drop-enabled="true"], [data-untargeted-drop-enabled="true"]';

export function Hand({
  cards,
  stagedIds,
  remainingEnergy,
  selectedId,
  onSelect,
  draggingId,
  planningDisabled,
  onPointerDragStart,
  onPointerDrop,
  onPointerDragEnd,
  returnDropId,
  flights,
  drawCue,
}: HandProps): ReactElement {
  const desktopHover = useDesktopHover();
  const [detailId, setDetailId] = useState<InstanceId | undefined>(undefined);
  const hover = useHoverPopover<{ id: InstanceId; anchor: HTMLElement }>({
    openDelayMs: FULL_CARD_HOVER_OPEN_DELAY_MS,
    dismissOnScrollOrDrag: true,
    // Re-opened on every mouse move with a fresh object: the same card is the
    // same preview, not a hand-off.
    isSame: (current, next) => current.id === next.id && current.anchor === next.anchor,
  });
  const [dragPreview, setDragPreview] = useState<DragPreview | undefined>(undefined);
  /** Card lifted out of the hand layout; a movable placeholder now owns its slot. */
  const [carriedId, setCarriedId] = useState<InstanceId | undefined>(undefined);
  const [cardOrder, setCardOrder] = useState<readonly InstanceId[]>(() =>
    cards.map(({ instance }) => instance.id));
  const pointerDragRef = useRef<PointerDrag | undefined>(undefined);
  /** DOM order frozen for the lifetime of a carry so pointer capture survives. */
  const dragStartOrderRef = useRef<readonly InstanceId[] | undefined>(undefined);
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);
  const handListRef = useRef<HTMLDivElement | null>(null);
  const reorderGeometryRef = useRef<HandReorderGeometry | undefined>(undefined);
  const lastReorderSlotRef = useRef<number | undefined>(undefined);
  const pendingReorderRef = useRef<{ instanceId: InstanceId; clientX: number; clientY: number } | undefined>(undefined);
  const reorderFrameRef = useRef<number | undefined>(undefined);
  const flipPositionsRef = useRef(new Map<string, { left: number; top: number }>());
  /** Last explicit draw cue consumed by this hand mount. */
  const drawnSequenceRef = useRef<number | undefined>(undefined);
  // Cards dealt by a draw flight that has not landed yet, and then — for one
  // glint — the ones that just have: a fresh card is the one the eye should find.
  const dealtIdsRef = useRef(new Set<InstanceId>());
  const [freshIds, setFreshIds] = useState<ReadonlySet<InstanceId>>(() => new Set());
  // A card the player tried to take without the energy for it shivers once and
  // blinks its red coin (03 §3.1), then the mark is retired.
  const [deniedId, setDeniedId] = useState<InstanceId | undefined>(undefined);
  const deniedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(deniedTimerRef.current), []);
  function deny(instanceId: InstanceId): void {
    clearTimeout(deniedTimerRef.current);
    setDeniedId(undefined);
    // A fresh frame between two denials, so a second try replays the shiver.
    requestAnimationFrame(() => setDeniedId(instanceId));
    deniedTimerRef.current = setTimeout(() => setDeniedId(undefined), MOTION_DURATIONS.base * 2);
  }
  useEffect(() => {
    const landed = [...flights.landingIds].filter((id) => dealtIdsRef.current.has(id));
    if (landed.length === 0) return;
    for (const id of landed) dealtIdsRef.current.delete(id);
    setFreshIds((current) => new Set([...current, ...landed]));
  }, [flights.landingIds]);
  const pointerDropTargetRef = useRef<HTMLElement | undefined>(undefined);
  const pendingDropTargetPointRef = useRef<{ clientX: number; clientY: number } | undefined>(undefined);
  const dropTargetFrameRef = useRef<number | undefined>(undefined);
  const suppressClickRef = useRef(false);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  // A card this cue has just drawn is not in the hand yet in any visual sense:
  // its overlay ghost is only now entering the screen. So its slot has
  // to be hidden by the very commit that mounts it. Deciding this in the layout
  // effect below (where the flight is launched) is one commit too late: the card
  // is painted in the hand at full opacity and then fades out over the carried
  // card's 90ms as its own flight sets off — which reads as the card arriving
  // before it flew. The effect clears this again for the keyframe fallback.
  const drawingId = drawCue !== undefined && drawnSequenceRef.current !== drawCue.sequence
    ? drawCue.instanceId
    : undefined;
  const detailCard = detailId === undefined
    ? undefined
    : cards.find(({ instance }) => instance.id === detailId);
  const detailData = detailCard === undefined ? undefined : cardInfoData(detailCard);
  const detailStaged = detailId !== undefined && stagedIds.has(detailId);
  const detailInsufficientEnergy = detailCard !== undefined
    && !detailStaged
    && !planningDisabled
    && detailCard.cost !== undefined
    && detailCard.cost > remainingEnergy;
  const previewCard = dragPreview === undefined
    ? undefined
    : cards.find(({ instance }) => instance.id === dragPreview.instanceId);
  const hoverCard = hover.value === undefined
    ? undefined
    : cards.find(({ instance }) => instance.id === hover.value!.id);
  const hoverData = hoverCard === undefined ? undefined : cardInfoData(hoverCard);
  // Four arrays and two maps, so they are not rebuilt by re-renders that have
  // nothing to do with the hand's contents or its order — a playback cue, a hover
  // popover, a targeting step. `cards` must stay identity-stable across those for
  // this to hold, which is why GameView memoizes it. A reorder frame does
  // invalidate this (cardOrder is exactly what changed), and that is the point.
  //
  // The carried card's frozen order is read from its ref on purpose: it is written
  // on pointerdown, one commit before `carriedId` turns defined, so recomputing
  // when that flips is enough to pick it up.
  const { authoritativeOrder, renderedOrder, visibleCards, visualOrderIndex } = useMemo(() => {
    const authoritative = cards.map(({ instance }) => instance.id);
    const rendered = reconcileHandOrder(cardOrder, authoritative);
    const stableDragOrder = carriedId === undefined
      ? rendered
      : reconcileHandOrder(dragStartOrderRef.current ?? rendered, authoritative);
    const cardsById = new Map(cards.map((card) => [card.instance.id, card]));
    return {
      authoritativeOrder: authoritative,
      renderedOrder: rendered,
      visibleCards: stableDragOrder
        .filter((instanceId) => !stagedIds.has(instanceId))
        .flatMap((instanceId) => {
          const card = cardsById.get(instanceId);
          return card === undefined ? [] : [card];
        }),
      visualOrderIndex: new Map(rendered
        .filter((instanceId) => !stagedIds.has(instanceId))
        .map((instanceId, index) => [instanceId, index])),
    };
  }, [cards, cardOrder, carriedId, stagedIds]);

  useEffect(() => {
    setCardOrder((previous) => reconcileHandOrder(
      previous,
      cards.map(({ instance }) => instance.id),
    ));
  }, [cards]);

  useEffect(() => () => {
    if (reorderFrameRef.current !== undefined) {
      globalThis.cancelAnimationFrame?.(reorderFrameRef.current);
    }
    if (dropTargetFrameRef.current !== undefined) {
      globalThis.cancelAnimationFrame?.(dropTargetFrameRef.current);
    }
  }, []);

  /** Read the fixed physical hand slots once for the current carry. */
  function measureReorderGeometry(): HandReorderGeometry | undefined {
    const list = handListRef.current;
    if (list === null) return undefined;
    const listRect = list.getBoundingClientRect();
    const margin = listRect.height <= 0 ? REORDER_ZONE_MARGIN : Math.max(REORDER_ZONE_MARGIN, listRect.height / 2);
    // offset geometry deliberately ignores FLIP transforms. Sorting physical
    // centres produces stable 0..N slots even after CSS `order` changes which
    // card occupies a given grid cell.
    const slots = [...list.querySelectorAll<HTMLElement>('[data-hand-order-id]')]
      .flatMap((anchor) => {
        const width = anchor.offsetWidth;
        const height = anchor.offsetHeight;
        // Real cards always have offset geometry. JSDOM and a few exotic
        // layout states do not, so retain the client-rect fallback while still
        // keeping the expensive read out of the per-move path.
        if (width <= 0 || height <= 0) {
          const rect = anchor.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return [];
          return [{ centerX: rect.left + rect.width / 2, centerY: rect.top + rect.height / 2 }];
        }
        return [{
          centerX: listRect.left + anchor.offsetLeft - list.scrollLeft + width / 2,
          centerY: listRect.top + anchor.offsetTop - list.scrollTop + height / 2,
        }];
      })
      .sort((a, b) => a.centerY - b.centerY || a.centerX - b.centerX)
      .map((slot, index) => ({ ...slot, index }));
    // A detached/just-mounted list may not have its own box yet even though
    // its children do. In that short window, keep the reorder zone around the
    // measured slots rather than treating every pointer point as outside.
    const hasListBounds = listRect.width > 0 && listRect.height > 0;
    return {
      left: hasListBounds ? listRect.left : Math.min(...slots.map((slot) => slot.centerX)),
      right: hasListBounds ? listRect.right : Math.max(...slots.map((slot) => slot.centerX)),
      top: hasListBounds ? listRect.top : Math.min(...slots.map((slot) => slot.centerY)),
      bottom: hasListBounds ? listRect.bottom : Math.max(...slots.map((slot) => slot.centerY)),
      margin,
      slots,
    };
  }

  function resetPointerReorder(): void {
    pendingReorderRef.current = undefined;
    if (reorderFrameRef.current !== undefined) {
      globalThis.cancelAnimationFrame?.(reorderFrameRef.current);
      reorderFrameRef.current = undefined;
    }
    reorderGeometryRef.current = undefined;
    lastReorderSlotRef.current = undefined;
  }

  /** Apply the most recent sensor point before a release commits its hand order. */
  function flushPointerReorder(): void {
    if (reorderFrameRef.current !== undefined) {
      globalThis.cancelAnimationFrame?.(reorderFrameRef.current);
      reorderFrameRef.current = undefined;
    }
    const pending = pendingReorderRef.current;
    pendingReorderRef.current = undefined;
    if (pending !== undefined) reorderCardAtPoint(pending.instanceId, pending.clientX, pending.clientY);
  }

  /** Move the placeholder to the nearest fixed layout slot. */
  function reorderCardAtPoint(instanceId: InstanceId, clientX: number, clientY: number): void {
    const geometry = reorderGeometryRef.current ?? measureReorderGeometry();
    // Do not freeze an empty just-mounted measurement for the carry: a later
    // frame can have real slot geometry once the layout has caught up.
    if (geometry === undefined || geometry.slots.length === 0) return;
    reorderGeometryRef.current = geometry;
    // A card carried over the board is not being reordered. Its slot simply
    // stays put until the finger returns to the hand's generous reorder zone.
    if (clientX < geometry.left - geometry.margin
      || clientX > geometry.right + geometry.margin
      || clientY < geometry.top - geometry.margin
      || clientY > geometry.bottom + geometry.margin) return;
    const targetIndex = nearestHandSlotIndex(geometry.slots, clientX, clientY);
    if (targetIndex === undefined || targetIndex === lastReorderSlotRef.current) return;
    lastReorderSlotRef.current = targetIndex;
    setCardOrder((previous) => moveHandCardToIndex(
      reconcileHandOrder(previous, authoritativeOrder),
      instanceId,
      targetIndex,
    ));
  }

  /** Coalesce high-frequency touch sensors to one potential reorder per frame. */
  function schedulePointerReorder(instanceId: InstanceId, clientX: number, clientY: number): void {
    pendingReorderRef.current = { instanceId, clientX, clientY };
    if (reorderFrameRef.current !== undefined) return;
    if (globalThis.requestAnimationFrame === undefined) {
      const pending = pendingReorderRef.current;
      pendingReorderRef.current = undefined;
      if (pending !== undefined) reorderCardAtPoint(pending.instanceId, pending.clientX, pending.clientY);
      return;
    }
    reorderFrameRef.current = globalThis.requestAnimationFrame(() => {
      reorderFrameRef.current = undefined;
      const pending = pendingReorderRef.current;
      pendingReorderRef.current = undefined;
      const drag = pointerDragRef.current;
      if (pending === undefined || drag?.carried !== true || drag.instanceId !== pending.instanceId) return;
      reorderCardAtPoint(pending.instanceId, pending.clientX, pending.clientY);
    });
  }

  function clearPointerDropTarget(): void {
    pointerDropTargetRef.current?.removeAttribute('data-pointer-drag-over');
    pointerDropTargetRef.current = undefined;
    pinDropZoneRect(undefined);
  }

  /**
   * Drops the queued hit test. Deliberately does NOT invalidate the measurement:
   * a release cancels the pending frame and only then resolves the drop, and the
   * drop must see the same boxes the highlight did. The measurement is dropped at
   * the start of the next gesture instead.
   */
  function cancelPointerDropTargetUpdate(): void {
    pendingDropTargetPointRef.current = undefined;
    if (dropTargetFrameRef.current !== undefined) {
      globalThis.cancelAnimationFrame?.(dropTargetFrameRef.current);
      dropTargetFrameRef.current = undefined;
    }
  }

  function updatePointerDropTarget(clientX: number, clientY: number): void {
    // The same resolver GameView commits through, so what lights up under the
    // finger is exactly what releasing there will do — see ./dropZones.ts.
    const next = resolveDropCandidate(clientX, clientY)
      ?.closest<HTMLElement>(DROP_ZONE_SELECTOR) ?? undefined;
    if (next === pointerDropTargetRef.current) return;
    clearPointerDropTarget();
    next?.setAttribute('data-pointer-drag-over', 'true');
    pointerDropTargetRef.current = next;
    pinDropZoneRect(next);
  }

  /** Hit-test at the paint rate, not at the touch sensor's raw event rate. */
  function schedulePointerDropTargetUpdate(clientX: number, clientY: number): void {
    pendingDropTargetPointRef.current = { clientX, clientY };
    if (dropTargetFrameRef.current !== undefined) return;
    if (globalThis.requestAnimationFrame === undefined) {
      const pending = pendingDropTargetPointRef.current;
      pendingDropTargetPointRef.current = undefined;
      if (pending !== undefined) updatePointerDropTarget(pending.clientX, pending.clientY);
      return;
    }
    dropTargetFrameRef.current = globalThis.requestAnimationFrame(() => {
      dropTargetFrameRef.current = undefined;
      const pending = pendingDropTargetPointRef.current;
      pendingDropTargetPointRef.current = undefined;
      if (pointerDragRef.current?.carried !== true || pending === undefined) return;
      updatePointerDropTarget(pending.clientX, pending.clientY);
    });
  }

  function clearPointerDrag(): void {
    const drag = pointerDragRef.current;
    pointerDragRef.current = undefined;
    resetPointerReorder();
    cancelPointerDropTargetUpdate();
    clearPointerDropTarget();
    setDragPreview(undefined);
    setCarriedId(undefined);
    dragStartOrderRef.current = undefined;
    if (drag?.started === true) onPointerDragEnd();
  }

  /**
   * Hands the released preview over to the shared flight system: the ghost
   * homes onto wherever the card now lives (staged cell card, hand slot) or
   * parks and dissolves when it has no visible home (a cast spell).
   */
  function launchFlight(instanceId: InstanceId, motion: CardDragMotion): void {
    flights.launch(instanceId, motion, flightOriginFromPreview(dragPreviewRef.current, motion));
  }

  function startPointerDrag(drag: PointerDrag, source: HTMLDivElement): void {
    if (drag.started) return;
    drag.started = true;
    // Freeze the physical slots before React swaps the lifted card for its
    // placeholder. This avoids a post-pickup layout read and keeps every slot
    // (including the carried card's former one) available to the reorder math.
    const geometry = measureReorderGeometry();
    if (geometry !== undefined && geometry.slots.length > 0) {
      reorderGeometryRef.current = geometry;
    }
    flights.cancel(drag.instanceId);
    resetCardPointerMotion(source);
    hover.closeNow();
    onPointerDragStart(drag.instanceId);
    setDragPreview({ instanceId: drag.instanceId, motion: drag.motion });
  }

  function handlePointerDown(
    instanceId: InstanceId,
    staged: boolean,
    event: ReactPointerEvent<HTMLDivElement>,
  ): void {
    const isPrimaryPointer = event.isPrimary !== false && event.button === 0;
    const isTouch = event.pointerType === 'touch';
    if (isPrimaryPointer) suppressClickRef.current = false;
    if (
      staged ||
      planningDisabled ||
      !isPrimaryPointer
    ) return;
    resetPointerReorder();
    cancelPointerDropTargetUpdate();
    clearPointerDropTarget();
    // A gesture boundary is the one moment nothing is lifted, so it is the only
    // safe moment to re-measure the drop zones from.
    invalidateDropZones();
    dragStartOrderRef.current = renderedOrder;
    const rect = event.currentTarget.getBoundingClientRect();
    const originX = rect.width <= 0 ? 50 : (event.clientX - rect.left) / rect.width * 100;
    const originY = rect.height <= 0 ? 70 : (event.clientY - rect.top) / rect.height * 100;
    const motion = createCardDragMotion(
      event.clientX,
      event.clientY,
      2,
      originX,
      originY,
    );
    // The preview lifts out of the slot instead of materializing at the anchor.
    liftCardDragMotionFromRect(motion, rect, previewAnchorGap());
    pointerDragRef.current = {
      instanceId,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      motion,
      started: false,
      carried: false,
      lastX: event.clientX,
      lastY: event.clientY,
    };
    // A playable card owns the touch from the first contact. Combined with
    // touch-action:none this keeps mobile browsers from converting an intended
    // pickup into native hand/page scrolling and cancelling the pointer.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Capture is an enhancement; moves still arrive while over the card.
    }
    // Touch keeps the existing immediate pickup. Mouse and pen wait for the
    // slop threshold so an ordinary desktop click remains visually unchanged.
    if (isTouch) startPointerDrag(pointerDragRef.current, event.currentTarget);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = pointerDragRef.current;
    if (drag === undefined || drag.pointerId !== event.pointerId) {
      if (desktopHover && event.pointerType !== 'touch' && draggingId === undefined) {
        applyCardPointerMotion(event.currentTarget, event.clientX, event.clientY);
      }
      return;
    }
    event.preventDefault();
    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    // A genuine move (not tap jitter) lifts the card out of the hand layout.
    // Once the carry has begun, the slop radius must not create a dead zone
    // around the pickup point: the finger returning to the original slot has
    // to keep reordering, otherwise a card can never be put back where it was.
    if (!drag.carried
      && distance >= POINTER_TAP_SLOP) {
      startPointerDrag(drag, event.currentTarget);
      drag.carried = true;
      setCarriedId(drag.instanceId);
    }
    if (drag.carried) {
      setCardDragTarget(drag.motion, event.clientX, event.clientY);
      schedulePointerDropTargetUpdate(event.clientX, event.clientY);
      schedulePointerReorder(drag.instanceId, event.clientX, event.clientY);
    }
  }

  /**
   * Lands a carried card at the given point: the drop is offered to the board
   * and the preview becomes a flight. Shared by the ordinary release and by a
   * cancelled carry, which must land rather than evaporate.
   */
  function resolvePointerDrop(drag: PointerDrag, clientX: number, clientY: number): void {
    suppressClickRef.current = true;
    onPointerDrop(drag.instanceId, clientX, clientY);
    // Every release becomes a flight: the ghost homes onto the staged cell
    // card (successful placement), the re-opened hand slot (miss/unplan) or
    // parks and dissolves (a cast spell). The movable placeholder becomes the
    // real card again in the same commit, so the ghost tracks its final slot.
    launchFlight(drag.instanceId, drag.motion);
    setCarriedId(undefined);
    setDragPreview(undefined);
    dragStartOrderRef.current = undefined;
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = pointerDragRef.current;
    if (drag === undefined || drag.pointerId !== event.pointerId) return;
    flushPointerReorder();
    pointerDragRef.current = undefined;
    resetPointerReorder();
    cancelPointerDropTargetUpdate();
    clearPointerDropTarget();
    // A begun carry always resolves as a drop, even when the pointer ends inside
    // the slop radius: the hand may already be reordered, and a click/tap release
    // here would discard the flight and pop the card details dialog instead.
    const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    if (drag.carried || distance >= POINTER_TAP_SLOP) {
      event.preventDefault();
      resolvePointerDrop(drag, event.clientX, event.clientY);
    } else {
      // Touch was visually picked up on pointerdown, but releasing without a
      // real move is still the ordinary click/tap handled by onClick.
      setCarriedId(undefined);
      setDragPreview(undefined);
      dragStartOrderRef.current = undefined;
      suppressClickRef.current = false;
      if (drag.started) onPointerDragEnd();
    }
  }

  function handlePointerCancel(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = pointerDragRef.current;
    if (drag === undefined || drag.pointerId !== event.pointerId) return;
    flushPointerReorder();
    pointerDragRef.current = undefined;
    resetPointerReorder();
    cancelPointerDropTargetUpdate();
    clearPointerDropTarget();
    // A carry that was already underway is not abandoned. On a phone the hand
    // list can scroll, and a browser that decides mid-gesture that the finger
    // was really scrolling steals the pointer and sends pointercancel — the card
    // used to snap back into the hand with the drop silently dropped. The last
    // position the finger was seen at is where the player aimed, so land there.
    if (drag.carried) resolvePointerDrop(drag, drag.lastX, drag.lastY);
    else {
      setDragPreview(undefined);
      setCarriedId(undefined);
      dragStartOrderRef.current = undefined;
      if (drag.started) onPointerDragEnd();
    }
  }

  function openDetails(instanceId: InstanceId, source: HTMLElement): void {
    previousFocusRef.current = source;
    setDetailId(instanceId);
  }

  function closeDetails(): void {
    setDetailId(undefined);
  }

  useEffect(() => {
    if (detailId !== undefined && detailCard === undefined) setDetailId(undefined);
  }, [detailCard, detailId]);

  useEffect(() => {
    const drag = pointerDragRef.current;
    if (
      drag !== undefined &&
      (planningDisabled || stagedIds.has(drag.instanceId) || !cards.some(({ instance }) => instance.id === drag.instanceId))
    ) {
      clearPointerDrag();
      suppressClickRef.current = false;
    }
  }, [cards, onPointerDragEnd, planningDisabled, stagedIds]);

  useEffect(() => {
    const element = dragPreviewRef.current;
    if (dragPreview === undefined || element === null) return;
    const instanceId = dragPreview.instanceId;
    return startCardDragAnimation(element, () => {
      const pointer = pointerDragRef.current;
      return pointer?.instanceId === instanceId ? pointer.motion : undefined;
    });
  }, [dragPreview]);

  useEffect(() => {
    const handleWindowBlur = (): void => {
      clearPointerDrag();
      suppressClickRef.current = false;
    };
    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, [onPointerDragEnd]);

  // While a pickup owns the finger, the hand must not also scroll. The cards
  // themselves carry touch-action:none, but the gaps between them in the mobile
  // grid do not, and a touch that begins on a card and drifts over a gap is
  // enough for the browser to start scrolling the list and take the pointer
  // away mid-drag. React attaches its own touchmove listener passively, so the
  // only place this can be refused is a non-passive listener of our own.
  useEffect(() => {
    const list = handListRef.current;
    if (list === null) return;
    const refuseScroll = (event: TouchEvent): void => {
      if (pointerDragRef.current === undefined || !event.cancelable) return;
      event.preventDefault();
    };
    list.addEventListener('touchmove', refuseScroll, { passive: false });
    return () => list.removeEventListener('touchmove', refuseScroll);
  }, []);

  // A resize re-flows the grid under every baseline this pass has recorded, and
  // nothing else here can notice: offsetLeft/offsetTop are measured against the
  // offsetParent, so the numbers look untouched while the slots have all moved.
  // Dropping the baselines costs no layout and makes the next pass re-record them
  // with no glide, which is the right answer for a resize anyway — the alternative
  // is one spurious slide across the whole hand at the next reorder.
  useEffect(() => {
    const forget = (): void => flipPositionsRef.current.clear();
    window.addEventListener('resize', forget);
    return () => window.removeEventListener('resize', forget);
  }, []);

  // FLIP: cards whose layout slot moved (placeholder reorder, return, draw or
  // play) glide from their previous slot to the new one instead of jumping — the
  // Balatro hand feel. Offsets are transform-free layout positions, so tilt/lift
  // effects don't pollute the deltas; the carried card itself is skipped (it is
  // out of flow).
  //
  // The dependency list is the whole point of the pass being affordable: the
  // offsetLeft/offsetTop reads below force a synchronous layout of the hand, and
  // this used to run after EVERY render. Picking a card up is the worst case —
  // mounting the floating preview re-renders the hand while the finger is already
  // moving, and that render bought a full layout for a strip whose slots had not
  // moved at all. Everything that can actually move a slot is here: the memo
  // output (hand contents, order, staged set, carried id), the carried id the loop
  // reads directly, and the draw cue whose flight this pass launches. What is
  // deliberately absent is the pickup/release/flight state — data-dragging,
  // data-landing and data-drawing are opacity-only rules, so they cannot move
  // anything — and `flights`, which GameView rebuilds on every one of its renders
  // (its `launch` only has to be current on the commit that changes drawCue, and
  // that commit is in the list).
  useLayoutEffect(() => {
    const list = handListRef.current;
    if (list === null) return;
    // During a carry the React DOM order stays frozen to protect pointer
    // capture. CSS order moves only the in-flow cards and placeholder; after
    // pointerup React reconciles the final semantic order once.
    for (const element of list.querySelectorAll<HTMLElement>('[data-hand-order-id]')) {
      const id = element.dataset.handOrderId;
      const order = id === undefined ? undefined : visualOrderIndex.get(id);
      const next = carriedId === undefined || order === undefined ? '' : String(order);
      // Reading inline style is free — no layout — while writing it is not
      // necessarily: engines differ on whether setting an identical value still
      // dirties the element. The offset reads below force one layout per pass
      // regardless, so this does not buy a layout-free frame; it keeps a no-op
      // write from dirtying every card in the strip before that one layout has to
      // resolve them all.
      if (element.style.order !== next) element.style.order = next;
    }
    const next = new Map<string, { left: number; top: number }>();
    let drawnElement: HTMLElement | undefined;
    // Invert every moved card first, then force layout ONCE for all of them, then
    // release them together. Forcing it inside the loop meant a full synchronous
    // layout of the page per moved card — with a dozen cards reordering on the
    // same frame that was a dozen layouts, and each one walks the whole hand.
    const inverted: HTMLElement[] = [];
    for (const element of list.querySelectorAll<HTMLElement>('[data-flip-id]')) {
      const id = element.dataset.flipId;
      if (id === undefined || element.dataset.carried === 'true') continue;
      const position = { left: element.offsetLeft, top: element.offsetTop };
      next.set(id, position);
      if (drawCue?.instanceId === id) drawnElement = element;
      const previous = flipPositionsRef.current.get(id);
      if (previous === undefined) {
        continue;
      }
      const dx = previous.left - position.left;
      const dy = previous.top - position.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
      element.style.transition = 'none';
      element.style.setProperty('--cards-flip-x', `${dx}px`);
      element.style.setProperty('--cards-flip-y', `${dy}px`);
      inverted.push(element);
    }
    if (inverted.length > 0) {
      // Force the inverted positions to paint before releasing them to animate.
      void list.offsetWidth;
      for (const element of inverted) {
        element.style.transition = '';
        element.style.setProperty('--cards-flip-x', '0px');
        element.style.setProperty('--cards-flip-y', '0px');
      }
    }
    flipPositionsRef.current = next;
    if (drawCue !== undefined
      && drawnSequenceRef.current !== drawCue.sequence
      && drawnElement !== undefined
      && drawnElement.dataset.dragging !== 'true') {
      drawnSequenceRef.current = drawCue.sequence;
      const drawn = drawnElement;
      // The hand scrolls vertically (a 3-per-row grid on mobile), so a card
      // drawn into an already overflowing hand lands in a row nobody can see.
      // Correcting the scroll here, before the draw-in's first frame, reveals
      // the slot without a visible jump. A carry owns the scroll position — the
      // dragging finger must never have it yanked away.
      //
      // Except on a phone. There the hand shows one-and-a-half rows while the player
      // scrolls by hand, and every draw — several at the top of a turn — dragged
      // it down to the newest card, throwing away wherever they had left it and
      // hiding the front of the hand. The draw flight is a full-screen ghost, so
      // the card is seen arriving regardless of which row is scrolled into view.
      if (carriedId === undefined && !compactBattleLayout()) {
        const overshoot = drawn.offsetTop + drawn.offsetHeight - (list.scrollTop + list.clientHeight);
        if (overshoot > 0) list.scrollTop += overshoot;
        else if (drawn.offsetTop < list.scrollTop) list.scrollTop = drawn.offsetTop;
      }
      // The trip itself cannot happen inside the hand: .cards-hand clips its
      // overflow and the whole region paints below the board and the panels, so
      // a card travelling in from off-screen spends nearly all of it invisible.
      // Fly it as the same fixed-position overlay ghost a released card uses,
      // measured after the scroll correction; the slot sits at opacity 0 under
      // it and cross-dissolves in when the ghost parks (see data-dragging).
      const slot = drawn.getBoundingClientRect();
      if (slot.width > 0 && slot.height > 0) {
        const origin = offscreenDrawOrigin(slot);
        const anchorGap = previewAnchorGap();
        // Flight coordinates anchor a card by its bottom edge plus that gap, so
        // this places the departing card centred on the launch point.
        const motion = createCardDragMotion(origin.x, origin.y + slot.height / 2 + anchorGap);
        flights.launch(drawCue.instanceId, motion, {
          width: slot.width,
          height: slot.height,
          anchorGap,
          dealt: true,
        });
        dealtIdsRef.current.add(drawCue.instanceId);
      } else {
        // Nothing re-renders after the fallback (no flight state changes), so the
        // render-level hide has to be lifted right here. The keyframe fills
        // backwards from opacity 0, so the handover is still seamless.
        delete drawn.dataset.drawing;
        drawn.dataset.drawn = 'true';
        drawn.addEventListener('animationend', function settle(event: AnimationEvent) {
          if (event.animationName !== 'cards-hand-card-draw-in') return;
          drawn.removeEventListener('animationend', settle);
          delete drawn.dataset.drawn;
        });
      }
    }
    // visualOrderIndex stands in for the rendered card list too: both come out of
    // the same useMemo, so they change identity together.
  }, [visualOrderIndex, carriedId, drawCue]);
  return (
    <section
      className="cards-hand"
      data-testid="hand"
      data-onboarding="hand"
      data-hand-drop-zone="true"
      data-return-active={returnDropId === undefined ? undefined : 'true'}
    >
      <div className="cards-hand-bar">
        <div className="cards-hand-title">
          ВАША РУКА · перетащите или выберите карту
        </div>
      </div>
      <div
        className="cards-hand-list"
        data-multi-row={visibleCards.length > 3 ? 'true' : 'false'}
        ref={handListRef}
      >
        {visibleCards.map(({ instance, cost, type, hasStats }) => {
        const selected = selectedId === instance.id;
        const carried = carriedId === instance.id;
        // The slot stays dimmed while its preview flies back home and un-dims
        // as the parked ghost dissolves on top of it (cross-dissolve).
        const dragging = draggingId === instance.id
          || dragPreview?.instanceId === instance.id
          || flights.flyingIds.has(instance.id);
        // A carried or already airborne card is hidden by its own rule and must
        // not be claimed by an incoming draw (the layout effect skips it too).
        const drawing = drawingId === instance.id && !carried && !dragging;
        const staged = stagedIds.has(instance.id);
        // Once the player is ready (and through the whole battle replay) the
        // "no energy" marking is moot and must not flicker with the replayed
        // energy values — planning is closed anyway.
        const insufficientEnergy = !staged
          && !planningDisabled
          && cost !== undefined
          && cost > remainingEnergy;
        const dragEnabled = !staged && !planningDisabled;
        const displayName = cardName(instance.defId);
        return (
          <Fragment key={instance.id}>
          {carried ? (
            <div
              className="cards-hand-reorder-placeholder"
              data-testid="hand-reorder-placeholder"
              data-hand-order-id={instance.id}
              data-flip-id={instance.id}
              aria-hidden="true"
            />
          ) : null}
          <div
            className="cards-hand-card"
            data-testid={`hand-card-${instance.id}`}
            data-card-type={type}
            data-token={instance.isToken === true ? 'true' : undefined}
            draggable={false}
            role="button"
            tabIndex={0}
            aria-pressed={selected && !planningDisabled}
            aria-haspopup="dialog"
            aria-label={insufficientEnergy
              ? `Открыть описание карты ${displayName}; не хватает энергии: нужно ${cost}, доступно ${remainingEnergy}`
              : `Открыть описание карты ${displayName}`}
            title={desktopHover ? undefined : `${displayName} — ${cardDescription(instance.defId) ?? 'Описание отсутствует'}`}
            data-staged={staged ? 'true' : 'false'}
            data-preparing={staged ? 'true' : undefined}
            data-planning-disabled={planningDisabled ? 'true' : undefined}
            data-insufficient-energy={insufficientEnergy ? 'true' : undefined}
            data-selected={selected ? 'true' : undefined}
            data-dragging={dragging ? 'true' : undefined}
            data-drawing={drawing ? 'true' : undefined}
            data-carried={carried ? 'true' : undefined}
            data-landing={flights.landingIds.has(instance.id) ? 'true' : undefined}
            data-fresh={freshIds.has(instance.id) ? 'true' : undefined}
            data-denied={deniedId === instance.id ? 'true' : undefined}
            onAnimationEnd={(event) => {
              // The glint on ::after is the fresh mark's last frame (the ring on ::before
              // is skipped for a staged card), so its end retires the mark.
              if (event.animationName !== 'cards-hand-card-fresh-glint' || !freshIds.has(instance.id)) return;
              setFreshIds((current) => {
                const next = new Set(current);
                next.delete(instance.id);
                return next;
              });
            }}
            data-hand-order-id={carried ? undefined : instance.id}
            data-flip-id={carried ? undefined : instance.id}
            data-drag-enabled={dragEnabled ? 'true' : undefined}
            onClick={(event) => {
              if (suppressClickRef.current && event.detail !== 0) {
                suppressClickRef.current = false;
                event.preventDefault();
                return;
              }
              suppressClickRef.current = false;
              if (desktopHover) {
                if (insufficientEnergy) deny(instance.id);
                else if (!staged && !planningDisabled) onSelect(instance.id);
              } else {
                openDetails(instance.id, event.currentTarget);
              }
            }}
            onKeyDown={(event) => {
              // Q6 (04 §6.3): Enter/Space take or put back the card, I opens it.
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (insufficientEnergy) deny(instance.id);
                else if (!staged && !planningDisabled) onSelect(instance.id);
              } else if (event.code === 'KeyI' && !event.altKey && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                openDetails(instance.id, event.currentTarget);
              }
            }}
            onMouseEnter={(event) => {
              if (desktopHover && draggingId === undefined) hover.open({ id: instance.id, anchor: event.currentTarget });
            }}
            onMouseMove={(event) => {
              if (desktopHover && draggingId === undefined && pointerDragRef.current === undefined) {
                hover.open({ id: instance.id, anchor: event.currentTarget });
              }
            }}
            onMouseLeave={(event) => {
              resetCardPointerMotion(event.currentTarget);
              hover.scheduleClose();
            }}
            onPointerDown={(event) => handlePointerDown(instance.id, staged, event)}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={(event) => {
              resetCardPointerMotion(event.currentTarget);
              handlePointerCancel(event);
            }}
            onLostPointerCapture={(event) => {
              resetCardPointerMotion(event.currentTarget);
              handlePointerCancel(event);
            }}
          >
            {/* No dedicated "НЕТ ⚡" badge: an unaffordable card marks itself
               by turning the corner energy cost red (data-insufficient-energy). */}
            <HandCardFace
              defId={instance.defId}
              cost={cost}
              type={type}
              hasStats={hasStats}
              attack={instance.stats.attack}
              health={instance.stats.health}
              statTestIdPrefix={`hand-card-${instance.id}`}
            />
          </div>
          </Fragment>
        );
      })}
      </div>
      {hover.value !== undefined && hoverData !== undefined ? (
        <CardInfoPopover
          anchor={hover.value.anchor}
          data={hoverData}
          closing={hover.closing}
          testId={`hand-card-${hover.value.id}-popover`}
          onMouseEnter={hover.cancelClose}
          onMouseLeave={hover.scheduleClose}
        />
      ) : null}
      {dragPreview !== undefined && previewCard !== undefined ? (
        <div
          ref={dragPreviewRef}
          className="cards-touch-drag-preview"
          data-testid="touch-drag-preview"
          data-card-type={previewCard.type}
          aria-hidden="true"
          // Pointer coordinates are per-frame interaction geometry (position
          // rides --cards-drag-x/y in the transform chain); static preview
          // presentation lives in KIT_CSS.
          style={cardDragMotionVariables(dragPreview.motion) as CSSProperties}
        >
          <HandCardFace
            defId={previewCard.instance.defId}
            cost={previewCard.cost}
            type={previewCard.type}
            hasStats={previewCard.hasStats}
            attack={previewCard.instance.stats.attack}
            health={previewCard.instance.stats.health}
            nameAnimated={false}
          />
        </div>
      ) : null}
      {detailCard !== undefined && detailData !== undefined ? (
        <CardInfoDialog
          data={detailData}
          testId="card-detail"
          onClose={closeDetails}
          returnFocusRef={previousFocusRef}
          actions={(
            <Button
              type="button"
              variant="primary"
              data-testid="card-detail-select"
              className="cards-card-detail-select"
              disabled={detailStaged || planningDisabled || detailInsufficientEnergy}
              data-card-unavailable={detailStaged || detailInsufficientEnergy ? 'true' : undefined}
              data-planning-disabled={planningDisabled ? 'true' : undefined}
              onClick={() => {
                onSelect(detailCard.instance.id);
                closeDetails();
              }}
            >
              {detailStaged
                ? 'Уже в плане'
                : detailInsufficientEnergy
                  ? 'Не хватает энергии'
                  : selectedId === detailCard.instance.id
                  ? 'Снять выбор'
                  : 'Выбрать'}
            </Button>
          )}
        />
      ) : null}
    </section>
  );
}
